import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { Ref } from "react";
import { Button, Icon, Modal, SkeletonText, useToast } from "@astrum/ui";
import { bytesToMb, uploadLimitFor } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { downloadFile, fileUrl, isDesktop, uploadFileWithProgress } from "../lib/transport.js";
import { uploadFailure, uploadThrownMessage } from "../lib/upload-error.js";
import { isOpenable, isPendingScan, useScanPolling } from "../lib/scan-status.js";
import { ScanBadge } from "./ScanBadge.js";
import { UploadProgress } from "./UploadProgress.js";
import { downscaleImage } from "../lib/downscale-image.js";
import { useFileDrop } from "../lib/useFileDrop.js";
import "./attachment-list.css";

/**
 * Files attached to a channel, project, or task.
 *
 * Extracted from InfoPanel so a task's detail page can show its attachments
 * inline without inheriting the panel's chrome. One implementation means the
 * scan-status rules and the download behaviour cannot drift between the two
 * places a file shows up.
 */

export type Attachment = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploaderId: string;
  uploaderName: string;
  /** pending | clean | infected | failed. Pending files are listed but not openable. */
  scanStatus?: string;
  createdAt: string;
};

/** Exactly one of these identifies where a file hangs. */
export type AttachmentScope = { channelId?: string; projectId?: string; taskId?: string };

export interface AttachmentListHandle {
  /** Re-reads the list, e.g. after something else attached a file. */
  reload: () => void;
}

export interface AttachmentListProps {
  scope: AttachmentScope;
  canUpload?: boolean;
  /** Visual density: "panel" inside InfoPanel, "block" on a detail page. */
  variant?: "panel" | "block";
  /** Called after a successful upload or delete, for dependent counts. */
  onChange?: () => void;
  emptyText?: string;
  ref?: Ref<AttachmentListHandle>;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Enough to tell a spreadsheet from a screenshot at a glance. */
export function iconForType(contentType: string): "document" | "insights" | "archive" | "eye" {
  // Video shares "eye" with images: both are things you look at rather than
  // open into an editor, and the icon set has nothing video-specific yet.
  if (contentType.startsWith("image/") || contentType.startsWith("video/")) return "eye";
  if (contentType.includes("spreadsheet") || contentType.includes("excel") || contentType === "text/csv") {
    return "insights";
  }
  if (contentType.includes("zip")) return "archive";
  return "document";
}

export function AttachmentList({
  scope,
  canUpload = true,
  variant = "block",
  onChange,
  emptyText = "No files attached yet.",
  ref,
}: AttachmentListProps) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [files, setFiles] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ fileName: string; fraction: number } | null>(null);
  const [removing, setRemoving] = useState<Attachment | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const scopeQuery = Object.entries(scope)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  const load = useCallback(
    (quiet = false) => {
      if (!scopeQuery) return;
      // A background refresh (polling a pending scan) must not flash the skeleton.
      if (!quiet) setLoading(true);
      api
        .get<{ files: Attachment[] }>(`/files?${scopeQuery}`)
        .then((res) => setFiles(res.files))
        .catch(() => undefined)
        .finally(() => setLoading(false));
    },
    [scopeQuery],
  );

  useEffect(() => load(), [load]);

  // No realtime event exists for scan completion, so poll while any file is
  // still being scanned; it flips to openable once the server marks it clean.
  useScanPolling(files.some(isPendingScan), () => load(true));

  useImperativeHandle(ref, () => ({ reload: () => load() }), [load]);

  const upload = useCallback(
    (file: File) => {
      if (!scopeQuery) return;
      const limit = uploadLimitFor(file.type);
      if (file.size > limit) {
        toast(`${file.name}: files must be under ${bytesToMb(limit)}MB.`, { tone: "danger" });
        return;
      }
      setUploading(true);
      void downscaleImage(file).then((prepared) => {
        const form = new FormData();
        for (const [key, value] of Object.entries(scope)) {
          if (value) form.append(key, value);
        }
        form.append("file", prepared);

        // Sent through XHR so the bar can show real progress; the api helper
        // JSON-encodes its body and fetch cannot report upload progress.
        setProgress({ fileName: file.name, fraction: 0 });
        uploadFileWithProgress("/files", form, (fraction) => setProgress({ fileName: file.name, fraction }))
          .promise.then((result) => {
            if (!result.ok) throw uploadFailure(result);
            load();
            onChange?.();
            toast(`${file.name} attached.`);
          })
          .catch((error: unknown) => {
            toast(`${file.name}: ${uploadThrownMessage(error)}`, { tone: "danger" });
          })
          .finally(() => {
            setUploading(false);
            setProgress(null);
            if (inputRef.current) inputRef.current.value = "";
          });
      });
    },
    [scope, scopeQuery, load, onChange, toast],
  );

  const remove = useCallback(
    (file: Attachment) => {
      // Optimistic: the row goes immediately and comes back on failure. A
      // spinner on a delete you already committed to reads as hesitation.
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      api
        .delete(`/files/${file.id}`)
        .then(() => {
          onChange?.();
          toast(`${file.fileName} removed.`);
        })
        .catch((error: unknown) => {
          load();
          toast(error instanceof ApiError ? error.message : "Could not remove that file.", { tone: "danger" });
        });
    },
    [load, onChange, toast],
  );

  const confirmRemove = useCallback(() => {
    if (!removing) return;
    remove(removing);
    setRemoving(null);
  }, [removing, remove]);

  const { isOver: isDraggingFileOver, dropZoneProps: attachmentsDropZoneProps } = useFileDrop(
    useCallback((dropped) => dropped.forEach((file) => upload(file)), [upload]),
  );

  return (
    <div
      className={`attachments attachments--${variant} ${canUpload && isDraggingFileOver ? "is-drop-target" : ""}`}
      {...(canUpload ? attachmentsDropZoneProps : {})}
    >
      <div className="attachments__head">
        <h4>Files · {files.length}</h4>
        {canUpload && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              aria-label="Upload files"
              className="attachments__input"
              onChange={(e) => {
                Array.from(e.target.files ?? []).forEach((file) => upload(file));
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              loading={uploading}
              icon={<Icon name="attach" size={14} />}
              onClick={() => inputRef.current?.click()}
            >
              Attach
            </Button>
          </>
        )}
      </div>

      {progress && <UploadProgress fileName={progress.fileName} fraction={progress.fraction} />}

      {loading ? (
        <SkeletonText lines={2} />
      ) : files.length === 0 ? (
        <p className="attachments__empty">{emptyText}</p>
      ) : (
        <ul className="attachments__list">
          {files.map((file) => {
            // Matches the server rule exactly, so no control is offered that
            // would come back 403.
            const canRemove = file.uploaderId === viewer?.id || viewer?.role === "admin";

            const openable = isOpenable(file);

            return (
              <li key={file.id}>
                {/* A plain anchor, not a router Link: this leaves the SPA to hit
                    the download endpoint, which responds with an attachment
                    rather than a page.

                    The desktop webview has no download manager to hand that
                    navigation to — the href stays for web and for
                    right-click/open-in-new-tab, but the click itself is
                    intercepted on desktop to fetch and save the file through
                    downloadFile instead. See transport.ts. */}
                <a
                  className={`attachments__file ${openable ? "" : "is-scanning"}`}
                  href={openable ? fileUrl(`/api/v1/files/${file.id}/download`) : undefined}
                  aria-disabled={openable ? undefined : true}
                  download={openable ? true : undefined}
                  onClick={
                    isDesktop && openable
                      ? (event) => {
                          event.preventDefault();
                          void downloadFile(`/api/v1/files/${file.id}/download`, file.fileName).catch(() =>
                            toast(`Could not download ${file.fileName}.`, { tone: "danger" }),
                          );
                        }
                      : undefined
                  }
                >
                  <Icon name={iconForType(file.contentType)} size={15} />
                  <span className="attachments__file-text">
                    <strong title={file.fileName}>{file.fileName}</strong>
                    <span>
                      {formatBytes(file.sizeBytes)} · {file.uploaderName}
                    </span>
                    <ScanBadge file={file} />
                  </span>
                  {openable && <Icon name="download" size={14} />}
                </a>

                {canRemove && (
                  <button
                    type="button"
                    className="attachments__remove"
                    aria-label={`Remove ${file.fileName}`}
                    title={`Remove ${file.fileName}`}
                    onClick={() => setRemoving(file)}
                  >
                    <Icon name="trash" size={13} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal open={!!removing} onClose={() => setRemoving(null)} title="Remove this file?">
        <p className="attachments__remove-copy">
          {removing ? `"${removing.fileName}" will be removed for everyone who could see it.` : ""} This cannot
          be undone.
        </p>
        <div className="attachments__remove-actions">
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove}>
            Remove file
          </Button>
        </div>
      </Modal>
    </div>
  );
}
